import api from './client';

export const REGISTRATION_FIELDS = [
  'phone', 'email', 'password', 'full_name', 'birth_date',
  'currency', 'promo_code', 'gender', 'address', 'terms_18plus',
] as const;
export const PAYMENT_METHODS = ['pix', 'upi', 'crypto', 'bank_transfer', 'skrill', 'neteller', 'cards'] as const;
export const SCREENSHOT_CATEGORIES = ['landing', 'registration', 'cashier', 'lobby', 'other'] as const;
export const BONUS_TYPES = ['non_sticky', 'sticky', 'no_deposit', 'none'] as const;
export const KYC_POLICIES = ['on_deposit', 'on_withdrawal', 'not_required'] as const;

export type ScreenshotCategory = (typeof SCREENSHOT_CATEGORIES)[number];
export type BonusType = (typeof BONUS_TYPES)[number];
export type KycPolicy = (typeof KYC_POLICIES)[number];

export interface FunnelAuditOffer {
  id: string;
  partner: string;
  brand: string | null;
  geo: string;
  offer_url: string | null;
  status: 'active' | 'paused';
}

export interface FunnelAuditBrand {
  id: string;
  name: string;
  name_key: string;
  created_at: string;
  updated_at: string;
}

export interface FunnelAudit {
  id: string;
  brand_id: string;
  offer_id: string | null;
  offer_ref: string;
  offer_partner: string;
  offer_brand: string | null;
  offer_url: string;
  geo: string;
  geos: string[];
  has_localization: number | null;
  reg_complexity_score: number | null;
  has_instant_one_click: number | null;
  registration_fields: string[];
  min_deposit_amount: number;
  min_deposit_currency: string;
  min_deposit_usd: number;
  fx_rate: number;
  fx_rate_at: string;
  payment_methods: string[];
  has_crash_games: number | null;
  bonus_type: BonusType | null;
  kyc_policy: KycPolicy | null;
  notes: string | null;
  created_by_name: string;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  screenshot_count?: number;
}

export interface FunnelAuditScreenshot {
  id: string;
  audit_id: string | null;
  category: ScreenshotCategory;
  original_name: string;
  content_type: string;
  size_bytes: number;
  sort_order: number;
}

export interface FunnelAuditInput {
  brand_id: string;
  offer_id: string | null;
  offer_url: string;
  geos: string[];
  has_localization: boolean | null;
  reg_complexity_score: number | null;
  has_instant_one_click: boolean | null;
  registration_fields: string[];
  min_deposit_amount: number;
  min_deposit_currency: string;
  payment_methods: string[];
  has_crash_games: boolean | null;
  bonus_type: BonusType | null;
  kyc_policy: KycPolicy | null;
  notes: string | null;
  screenshot_ids: string[];
}

export type PublicFunnelAuditInput = Omit<FunnelAuditInput, 'brand_id' | 'offer_id'> & {
  brand_name: string;
};

export interface FunnelAuditPublicLink {
  id: string;
  token: string;
  created_at: string;
}

export interface FunnelAuditListParams {
  page?: number;
  per_page?: number;
  latest_only?: boolean;
  search?: string;
  brand_id?: string;
  geo?: string;
  sort?: 'created_at' | 'min_deposit_usd' | 'reg_complexity_score' | 'geo' | 'brand';
  order?: 'asc' | 'desc';
}

function queryString(params: FunnelAuditListParams): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, String(value));
  }
  return query.toString();
}

export async function getFunnelAuditOffers(): Promise<FunnelAuditOffer[]> {
  return api.get<FunnelAuditOffer[]>('/admin/funnel-audits/offers');
}

export async function getFunnelAuditBrands(): Promise<FunnelAuditBrand[]> {
  return api.get<FunnelAuditBrand[]>('/admin/funnel-audits/brands');
}

export async function createFunnelAuditBrand(name: string): Promise<FunnelAuditBrand> {
  return api.post<FunnelAuditBrand>('/admin/funnel-audits/brands', { name });
}

export async function createFunnelAuditPublicLink(): Promise<FunnelAuditPublicLink> {
  return api.post<FunnelAuditPublicLink>('/admin/funnel-audits/public-links');
}

export async function getPublicFunnelAuditLink(token: string) {
  return api.get<{ owner_name: string }>(
    `/funnel-audits/public/${encodeURIComponent(token)}`,
    { skipAuth: true },
  );
}

export async function submitPublicFunnelAudit(token: string, input: PublicFunnelAuditInput) {
  return api.post<{ submitted: true }>(
    `/funnel-audits/public/${encodeURIComponent(token)}/submit`,
    input,
    { skipAuth: true },
  );
}

export async function uploadPublicFunnelAuditScreenshot(
  token: string,
  file: File,
  category: ScreenshotCategory,
) {
  const form = new FormData();
  form.append('file', file);
  form.append('category', category);
  return api.form<FunnelAuditScreenshot>(
    `/funnel-audits/public/${encodeURIComponent(token)}/media`,
    form,
    { skipAuth: true },
  );
}

export async function getPublicFunnelAuditScreenshot(token: string, id: string): Promise<Blob> {
  return api.blob(
    `/funnel-audits/public/${encodeURIComponent(token)}/media/${encodeURIComponent(id)}/raw`,
    { skipAuth: true },
  );
}

export async function getPublicFunnelAuditRate(token: string, currency: string) {
  return api.get<{ currency: string; rate: number; at: string }>(
    `/funnel-audits/public/${encodeURIComponent(token)}/rates/${encodeURIComponent(currency.toUpperCase())}`,
    { skipAuth: true },
  );
}

export async function getFunnelAudits(params: FunnelAuditListParams) {
  return api.get<{ items: FunnelAudit[]; total: number; page: number; per_page: number }>(
    `/admin/funnel-audits?${queryString(params)}`,
  );
}

export async function getFunnelAudit(id: string) {
  return api.get<{ audit: FunnelAudit; screenshots: FunnelAuditScreenshot[]; history: FunnelAudit[] }>(
    `/admin/funnel-audits/${id}`,
  );
}

export async function createFunnelAudit(input: FunnelAuditInput) {
  return api.post<{ id: string }>('/admin/funnel-audits', input);
}

export async function updateFunnelAudit(id: string, input: Partial<FunnelAuditInput>) {
  return api.patch<{ audit: FunnelAudit }>(`/admin/funnel-audits/${id}`, input);
}

export async function deleteFunnelAudit(id: string) {
  return api.delete<{ ok?: true; pending_approval?: boolean }>(`/admin/funnel-audits/${id}`);
}

export async function uploadFunnelAuditScreenshot(file: File, category: ScreenshotCategory) {
  const form = new FormData();
  form.append('file', file);
  form.append('category', category);
  return api.form<FunnelAuditScreenshot>('/admin/funnel-audits/media', form);
}

export async function getFunnelAuditScreenshot(id: string): Promise<Blob> {
  return api.blob(`/admin/funnel-audits/media/${id}/raw`);
}

export async function getFunnelAuditRate(currency: string) {
  return api.get<{ currency: string; rate: number; at: string }>(
    `/admin/funnel-audits/rates/${encodeURIComponent(currency.toUpperCase())}`,
  );
}

export async function downloadFunnelAuditsCsv(params: FunnelAuditListParams): Promise<void> {
  const blob = await api.blob(`/admin/funnel-audits/export.csv?${queryString(params)}`);
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = `funnel-audits-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(href), 0);
}
